'use client';

import { useState } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { ApiError } from '@lib/api/api-response';
import { useImportQueueHideCompleted } from '@lib/import-queue-ui/hooks/use-import-queue-hide-completed';
import type { ImportQueueController } from '@lib/import-queue-ui/types/import-queue-controller';
import { filterImportQueueItems } from '@lib/import-queue-ui/utils/filter-import-queue-items';
import { summarizeImportQueueItems } from '@lib/import-queue-ui/utils/summarize-import-queue-items';
import { MAX_INSIGHT_REPROCESS_BATCH } from '@lib/insights/constants/insight-upload';
import { invalidateInsightReprocessQueries } from '@lib/insights/hooks/invalidate-insight-reprocess-queries';
import { useInsightProcessingQueue } from '@lib/insights/hooks/use-insight-processing-queue';
import { useReprocessInsightDocument } from '@lib/insights/hooks/use-reprocess-insight-document';
import type { ReprocessBatchResult } from '@lib/insights/services/reprocess-insight-documents-batch';
import { useQueryClient } from '@lib/query/client';
import { useAuthedMutation } from '@lib/query/use-authed-query';
import { toast } from 'sonner';
import { useInsightsBulkImport } from '../context/insights-bulk-import-context';
import { mergeInsightQueueItems } from '../utils/merge-insight-queue-items';
import { resolveRetryAction } from '../utils/resolve-retry-action';
import { useInsightsImportQueueDismissals } from './use-insights-import-queue-dismissals';
function pickDocumentIdFromParams(value: string | string[] | undefined): string | undefined {
  if (typeof value === 'string' && value.length > 0) return value;
  if (Array.isArray(value) && value[0] && value[0].length > 0) return value[0];
  return undefined;
}
export function useInsightsBulkImportController(): ImportQueueController {
  const router = useRouter();
  const params = useParams<{
    document_id?: string | string[];
  }>();
  const activeNavigationId = pickDocumentIdFromParams(params?.document_id);
  const {
    queue,
    activeUploadCount,
    retryItem: retryClientItem,
    clearAll: clearClientQueue
  } = useInsightsBulkImport();
  // Session-only: a quick look at what failed, not a
  // preference worth remembering across visits.
  const [failedOnly, setFailedOnly] = useState(false);
  const {
    data: serverRows
  } = useInsightProcessingQueue();
  const {
    hideCompleted,
    setHideCompleted
  } = useImportQueueHideCompleted('insights');
  const {
    dismissedIds,
    dismissIds
  } = useInsightsImportQueueDismissals();
  const reprocessDocument = useReprocessInsightDocument();
  const queryClient = useQueryClient();
  const rows = serverRows ?? [];
  const mergedItems = mergeInsightQueueItems(queue, rows, new Date());
  const items = filterImportQueueItems(mergedItems, {
    hideCompleted,
    failedOnly,
    dismissedIds
  });
  // Counted before the list filters so the summary reports the
  // whole import, not just the rows still on screen.
  const counts = summarizeImportQueueItems(filterImportQueueItems(mergedItems, {
    hideCompleted: false,
    dismissedIds
  }));
  const progress = {
    ...counts,
    // A cleared row's upload keeps running, so the tab is
    // needed while any upload is in flight, listed or not.
    tabRequired: counts.uploading > 0 || (activeUploadCount ?? 0) > 0
  };
  const retryAll = useAuthedMutation({
    mutationFn: async (documentIds: string[]) => {
      // A large import can fail more documents than one request
      // may retry, so the set goes out in route-sized chunks,
      // one at a time, and the results are merged.
      //
      // A chunk that fails stops the run but keeps what earlier
      // chunks retried: those documents are already back in
      // the pipeline, so the rail has to refresh and the toast
      // has to count them. The rest are reported as not
      // retried.
      const merged: ReprocessBatchResult = {
        succeeded: [],
        failed: []
      };
      for (let start = 0; start < documentIds.length; start += MAX_INSIGHT_REPROCESS_BATCH) {
        const chunk = documentIds.slice(start, start + MAX_INSIGHT_REPROCESS_BATCH);
        let result: ReprocessBatchResult;
        try {
          const response = await fetch('/api/protected/insights/documents/reprocess-batch', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              documentIds: chunk
            })
          });
          if (!response.ok) {
            throw new ApiError('Failed to retry documents', response.status);
          }
          result = (await response.json()) as ReprocessBatchResult;
        } catch (error) {
          const reason = error instanceof Error ? error.message : 'Failed to retry documents';
          merged.failed.push(...documentIds.slice(start).map(id => ({
            id,
            reason
          })));
          break;
        }
        merged.succeeded.push(...result.succeeded);
        merged.failed.push(...result.failed);
      }
      return merged;
    },
    onSuccess: result_0 => {
      invalidateInsightReprocessQueries(queryClient);
      // The route answers 200 even when every document was
      // rejected, so a 2xx alone is not success. Without
      // this the rail re-renders unchanged and the user
      // clicks a button that silently does nothing.
      if (result_0.failed.length > 0) {
        toast.error(result_0.succeeded.length > 0 ? `Retried ${result_0.succeeded.length}, ${result_0.failed.length} could not be retried` : 'None of the documents could be retried', {
          description: result_0.failed[0]?.reason
        });
      }
    },
    onError: () => {
      toast.error('Could not retry the failed documents');
    }
  });

  // Server-side processing counts as active work too, so
  // the rail handle keeps its dot after the upload
  // finishes but before the pipeline completes.
  const hasActiveWork = mergedItems.some(item => item.status === 'queued' || item.status === 'uploading' || item.status === 'processing');

  // A clientId matching a server row's documentId means the
  // upload already committed, so retry re-runs the pipeline
  // instead of going through the client upload queue, which
  // has no entry for it after a refresh.
  function retryItem(clientId: string) {
    const action = resolveRetryAction(clientId, rows);
    if (action.type === 'reprocess') {
      reprocessDocument.mutate({
        documentId: action.documentId
      });
      return;
    }
    if (action.type === 'client-retry') {
      retryClientItem(clientId);
    }
    // 'blocked': the row already renders
    // retryDisabledReason instead of a button, so this
    // should be unreachable.
  }
  return {
    domainKey: 'insights',
    title: 'Insights imports',
    subtitle: 'Open files from this batch',
    items,
    progress,
    hasActiveWork,
    retryItem,
    retryAllFailed: () => {
      // The button counts every retryable failed row, so it
      // has to act on both origins. Server-backed rows go to
      // the batch endpoint; a client item that never got a
      // row has no document id to send and is re-uploaded
      // through the client queue instead. Handling only the
      // former makes the button a no-op for a batch of
      // upload failures.
      const serverIds = rows.filter(row => row.status === 'failed' && row.isRetryable).map(row_0 => row_0.documentId);
      if (serverIds.length > 0) retryAll.mutate(serverIds);
      for (const item_0 of mergedItems) {
        if (item_0.status !== 'failed' || item_0.retryDisabledReason) continue;
        if (resolveRetryAction(item_0.clientId, rows).type !== 'client-retry') {
          continue;
        }
        retryClientItem(item_0.clientId);
      }
    },
    listPreferences: {
      hideCompleted,
      onHideCompletedChange: setHideCompleted,
      failedOnly,
      onFailedOnlyChange: setFailedOnly,
      failedCount: counts.failed
    },
    listActions: {
      onClearAll: () => {
        const idsToDismiss = mergedItems.flatMap(item_1 => item_1.navigationId ? [item_1.clientId, item_1.navigationId] : [item_1.clientId]);
        dismissIds(idsToDismiss);
        clearClientQueue();
      },
      clearAllDisabled: mergedItems.length === 0,
      clearAllRequiresConfirm: true,
      clearAllConfirmTitle: 'Clear all imports?',
      clearAllConfirmDescription: 'Every item is removed from this list. Queued uploads are canceled. Files already uploading or processing in the background may still finish.'
    },
    activeNavigationId,
    onItemClick: clicked => {
      if (clicked.navigationId) {
        router.push(`/insights/${clicked.navigationId}`);
      }
    }
  };
}